import React, {PureComponent} from "react";
import styles from "./list-view.module.css";
import renderText from "../renderText";
import {LoadingIndicator} from "../loading/LoadingIndicator";
import { cssHelper } from "../../utils/cssHelper";
import isTouchDevice from "../../utils/isTouchDevice";
import scrollbar from "../../styles/scrollbars.module.css";

export type ListViewProps = {
    readonly pageSize: number;
    readonly scrollOffset: number;
}

export type ListViewState<TEntry> = {
    loading: boolean;
    allDataLoaded: boolean;
    entries: TEntry[];
}

export abstract class ListView<TEntry, TProps extends ListViewProps, TState extends ListViewState<TEntry>> extends PureComponent<TProps, TState> {
    protected readonly scrollView: React.RefObject<HTMLDivElement>;
    private page = 0;
    private isAttached = false;

    protected constructor(props: TProps, context: any) {
        super(props, context);

        this.scrollView = this.getScrollViewRef();
        this.scrollEventListener = this.scrollEventListener.bind(this);
        this.state = this.getInitialState();
    }

    componentDidMount() {
        if (this.scrollView.current) {
            this.scrollView.current.addEventListener('scroll', this.scrollEventListener, false);
            this.isAttached = true;
        }

        this.loadPage(0);
    }

    componentDidUpdate(prevProps: Readonly<TProps>, prevState: Readonly<TState>, snapshot?: any): void {
        if (this.isAttached)
            return;

        if (this.scrollView.current) {
            this.scrollView.current.addEventListener('scroll', this.scrollEventListener, false);
            this.isAttached = true;
        }
    }

    componentWillUnmount() {
        if (this.scrollView.current) {
            this.scrollView.current.removeEventListener('scroll', this.scrollEventListener, false);
        }
    }

    render() {
        if (!this.state.entries.length && this.state.allDataLoaded) {
            const noDataText = this.getNoDataText();
            if (noDataText) {
                return renderText(noDataText, 32, 45, 50, 80, styles.no_data);
            }
        }

        return this.renderInternal();
    }

    renderInternal() {
        const entries = this.renderEntries();

        return (
            <div className={cssHelper(styles.scroll_view).add(isTouchDevice() ? scrollbar.empty : scrollbar.thin).toString()} ref={this.scrollView}>
                {entries}

                <LoadingIndicator disabled={!this.state.loading}/>
            </div>
        );
    }

    renderEntries() {
        if (!this.state.entries || !this.state.entries.length)
            return;

        return (
            <div className={styles.entries}>
                {this.state.entries.map(entry => this.renderEntry(entry))}
            </div>
        )
    }

    getInitialState() {
        return {
            loading: false,
            allDataLoaded: false,
            entries: [] as TEntry[],
        } as TState;
    }

    getNoDataText(): string | undefined {
        return;
    }

    abstract renderEntry(entry: TEntry): JSX.Element | undefined;
    abstract loadPageInternal(page: number): Promise<{ entries: TEntry[], state?: Partial<TState> }>;

    protected trackError(message: string, error?: Error) {
        console.error(message, error);
    }

    private async loadPage(page: number) {
        try {
            this.setState({
                loading: true,
            });

            const {entries, state} = await this.loadPageInternal(page);

            this.setState({
                loading: false,
                allDataLoaded: this.hasAllDataLoaded(entries),
                ...state,
                entries: [
                    ...this.state.entries,
                    ...entries
                ]
            } as TState);
        } catch (error) {
            this.setState({
                loading: false,
                allDataLoaded: true,
            });

            this.trackError("Failed to load next page", error);
        }
    }

    protected hasAllDataLoaded(entries: TEntry[]) {
        return !entries || entries.length < this.props.pageSize;
    }

    protected getScrollViewRef(): React.RefObject<HTMLDivElement> {
        return React.createRef();
    }

    private scrollEventListener() {
        const canLoadNextPage = !this.state.loading
            && !this.state.allDataLoaded
            && this.scrollView.current
            && this.scrollView.current.scrollTop + this.scrollView.current.offsetHeight >= this.scrollView.current.scrollHeight - this.props.scrollOffset;

        if (canLoadNextPage) {
            this.loadPage(++this.page);
        }
    }
}

